// Create src/utils/reportGenerator.js
import { doc, updateDoc, addDoc, collection, getDocs, query, where, runTransaction, writeBatch } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// A driver's end-of-day report covers what they closed out on the round:
// packages delivered, plus any called off.
//
// Returns are deliberately NOT here. Returned packages go back to the store and
// are reconciled on the Returns page, which has its own verification flow; no
// report has ever included one (219 historical reports carry deliveredCount and
// cancelledCount only, and none carries a returned count).
//
// Exported so PrintDailySummary gates its button on the same rule. These two
// filters disagreeing is what produced "No packages found for this driver" on a
// button that looked enabled.
export const REPORTABLE_STATUSES = ['delivered', 'canceled'];

export const generateDriverReport = async (packages, userId, options = {}) => {
  const { shouldUpdatePackageStatus = false } = options;

  try {
    console.log('Starting report generation for user:', userId);
    console.log('Packages received:', packages.length);

    // Everything the driver closed out today that has not already been settled
    // by an earlier report.
    //
    // `isPaid` is deliberately NOT an entry condition. It is an OUTPUT of this
    // function: the sweep below sets isPaid and moves delivered packages to
    // status "paid". Requiring it up front made the filter unsatisfiable, since
    // no package is ever both "delivered" and already paid - the first report a
    // package appeared in removed it from the pool permanently.
    //
    // driverReportId is the real "already reported" marker, stamped by the
    // sweep below, and is what stops a package being settled twice.
    const driverPackages = packages.filter(
      (pkg) =>
        REPORTABLE_STATUSES.includes((pkg.status || '').toLowerCase()) &&
        pkg.assignedTo === userId &&
        !pkg.driverReportId
    );

    console.log('Filtered driver packages:', driverPackages.length);

    if (driverPackages.length === 0) {
      throw new Error('No packages found for this driver');
    }

    // Only delivered packages represent money collected. Canceled packages
    // appear on the report as line items but are never paid for.
    const deliveredPackages = driverPackages.filter(
      pkg => (pkg.status || '').toLowerCase() === "delivered"
    );

    const totalDeliveredPrice = deliveredPackages.reduce(
      (sum, pkg) => sum + (Number(pkg.total) || 0), 0
    );

    // Fetch payment methods for each package
    const fetchPaymentMethods = async (packageIds) => {
      if (!packageIds.length) return {};
      const paymentMap = {};
      const batchSize = 30;
      for (let i = 0; i < packageIds.length; i += batchSize) {
        const batchIds = packageIds.slice(i, i + batchSize);
        const paymentsQuery = query(
          collection(db, "payments"),
          where("packageId", "in", batchIds)
        );
        const snapshot = await getDocs(paymentsQuery);
        snapshot.forEach(doc => {
          const data = doc.data();
          paymentMap[data.packageId] = data.paymentMethod || "N/A";
        });
      }
      return paymentMap;
    };

    // 1. Fetch payment methods
    const packageIds = driverPackages.map(pkg => pkg.id);
    let paymentMethods = {};
    try {
      paymentMethods = await fetchPaymentMethods(packageIds);
    } catch (e) {
      console.error("Failed to fetch payment methods", e);
    }

    // Calculate subtotals by payment method (only for delivered packages)
    const paymentSubtotals = {};
    deliveredPackages.forEach(pkg => {
      const paymentMethod = paymentMethods[pkg.id] || 'N/A';
      const formattedMethod = formatPaymentMethod(paymentMethod);
      const price = Number(pkg.total) || 0;
      
      if (!paymentSubtotals[formattedMethod]) {
        paymentSubtotals[formattedMethod] = {
          count: 0,
          total: 0
        };
      }
      
      paymentSubtotals[formattedMethod].count += 1;
      paymentSubtotals[formattedMethod].total += price;
    });

    // Get invoice number before generating PDF
    let invoiceNumber = '';
    await runTransaction(db, async (transaction) => {
      const invoiceNumberRef = doc(db, 'driverReports', 'invoiceNumber');
      const invoiceNumDoc = await transaction.get(invoiceNumberRef);
      if (!invoiceNumDoc.exists()) {
        throw new Error('Invoice number not found');
      }

      const currentNumber = invoiceNumDoc.data().number;
      invoiceNumber = 'INV-' + String(currentNumber).padStart(6, '0');

      transaction.update(invoiceNumberRef, {
        number: currentNumber + 1
      });
    });

    // 2. Generate PDF
    const docPDF = new jsPDF();
    
    // Header section with title and invoice number
    docPDF.setFontSize(18);
    docPDF.text('Driver Summary Report', 14, 18);
    
    // Invoice number in top right corner
    docPDF.setFontSize(12);
    const pageWidth = docPDF.internal.pageSize.width;
    const invoiceText = invoiceNumber;
    const invoiceTextWidth = docPDF.getTextWidth(invoiceText);
    docPDF.text(invoiceText, pageWidth - invoiceTextWidth - 14, 18);
    
    // Date and package count
    docPDF.setFontSize(12);
    docPDF.text(`Date: ${new Date().toLocaleDateString()}`, 14, 28);
    docPDF.text(`Total Packages: ${driverPackages.length}`, 14, 36);

    // Table data
    const tableColumn = ["Date", "Tracking #", "Status", "Recipient", "Destination", "Price", "Payment Method"];
    const tableRows = driverPackages.map(pkg => [
      pkg.createdAt?.toDate?.()?.toLocaleDateString('en-GB') || 'N/A',
      pkg.trackingNumber,
      pkg.status,
      pkg.recipient,
      pkg.destination || 'N/A',
      pkg.total !== undefined ? `$${Number(pkg.total).toFixed(2)}` : 'N/A',
      paymentMethods[pkg.id] ? formatPaymentMethod(paymentMethods[pkg.id]) : 'No payment method recorded'
    ]);

    autoTable(docPDF, {
      head: [tableColumn],
      body: tableRows,
      startY: 44,
      styles: { fontSize: 10 }
    });

    // Totals section
    let finalY = docPDF.lastAutoTable.finalY || 44;
    
    // Basic totals
    docPDF.text(`Total Delivered: ${deliveredPackages.length}`, 14, finalY + 12);
    docPDF.text(
      `Total Cancelled: ${driverPackages.filter(pkg => pkg.status === "canceled").length}`,
      80,
      finalY + 12
    );

    // Payment method subtotals
    finalY += 25;
    docPDF.setFontSize(14);
    docPDF.text('Payment Method Breakdown:', 14, finalY);
    docPDF.setFontSize(12);
    
    finalY += 10;
    let subtotalY = finalY;
    
    Object.entries(paymentSubtotals).forEach(([method, data]) => {
      docPDF.text(`${method}: ${data.count} packages - $${data.total.toFixed(2)}`, 14, subtotalY);
      subtotalY += 8;
    });

    // Grand total
    subtotalY += 5;
    docPDF.setFontSize(14);
    docPDF.text(`Grand Total: $${totalDeliveredPrice.toFixed(2)}`, 14, subtotalY);

    // 3. Convert PDF to blob for upload
    const pdfBlob = docPDF.output('blob');
    const fileName = `${invoiceNumber}-${Date.now()}-driver-summary.pdf`;
    
    // 4. Upload to Firebase Storage
    const storageRef = ref(storage, `driverReports/${userId}/${fileName}`);
    console.log('Uploading PDF to storage...');
    const uploadResult = await uploadBytes(storageRef, pdfBlob);
    const downloadURL = await getDownloadURL(uploadResult.ref);
    console.log('PDF uploaded successfully:', downloadURL);

    // 5. Save report metadata to Firestore (including payment subtotals)
    const reportData = {
      driverId: userId,
      reportDate: new Date(),
      packageCount: driverPackages.length,
      deliveredCount: deliveredPackages.length,
      cancelledCount: driverPackages.filter(pkg => (pkg.status || '').toLowerCase() === "canceled").length,
      totalDeliveredPrice: totalDeliveredPrice,
      paymentSubtotals: paymentSubtotals, // Add payment subtotals to report data
      pdfUrl: downloadURL,
      fileName: fileName,
      packageIds: packageIds,
      invoiceNumber: invoiceNumber, // Use the already-generated invoice number
      createdAt: new Date(),
      createdBy: userId
    };

    const reportDoc = await addDoc(collection(db, 'driverReports'), reportData);
    console.log('Report metadata saved with ID:', reportDoc.id);

    // ---- Settlement --------------------------------------------------------
    //
    // Every package in the report gets stamped with driverReportId so it cannot
    // be settled twice. Beyond that the two groups are treated differently:
    //
    //   delivered -> isPaid, status "paid", and a payment record
    //   canceled  -> stamped only. No money changed hands, so it is a line item
    //                on the report and nothing more.
    //
    // The package update and its payment are written in a single batch, so a
    // payment can never exist for a package that was not marked paid, and a
    // package can never be marked paid without its payment.

    // Which delivered packages already have a payment (taken at the door via
    // recordPayment)? Those get their existing record annotated rather than a
    // second one created.
    const existingPaymentDocs = {};
    const deliveredIds = deliveredPackages.map(p => p.id);
    for (let i = 0; i < deliveredIds.length; i += 30) {
      const batchIds = deliveredIds.slice(i, i + 30);
      try {
        const snapshot = await getDocs(query(
          collection(db, "payments"),
          where("packageId", "in", batchIds)
        ));
        snapshot.forEach(d => { existingPaymentDocs[d.data().packageId] = d.ref; });
      } catch (error) {
        console.error('Error looking up existing payments:', error);
      }
    }

    const summaryStamp = {
      driverSummaryUrl: downloadURL,
      driverSummaryId: reportDoc.id,
      driverSummaryInvoice: reportData.invoiceNumber,
      driverSummaryDate: new Date(),
      updatedAt: new Date(),
    };

    let paymentsCreated = 0;
    let paymentsAnnotated = 0;

    for (const pkg of deliveredPackages) {
      try {
        const batch = writeBatch(db);
        const existing = existingPaymentDocs[pkg.id];

        if (existing) {
          batch.update(existing, { isPaid: true, paidDate: new Date(), ...summaryStamp });
          paymentsAnnotated += 1;
        } else {
          // No payment was taken at the door, so the report is where this one
          // gets recorded. Method was never captured by this flow, so it is
          // left explicitly unknown rather than asserted as cash.
          const paymentRef = doc(collection(db, "payments"));
          batch.set(paymentRef, {
            packageId: pkg.id,
            amount: Number(pkg.total) || Number(pkg.cost) || 0,
            paymentMethod: 'unknown',
            paymentDate: new Date(),
            paidBy: userId,
            recordedBy: userId,
            notes: `Recorded by driver summary ${reportData.invoiceNumber}.`,
            verified: false,
            isPaid: true,
            paidDate: new Date(),
            createdAt: new Date(),
            ...summaryStamp,
          });
          paymentsCreated += 1;
        }

        batch.update(doc(db, "packages", pkg.id), {
          status: "paid",
          isPaid: true,
          paidDate: new Date(),
          driverReportId: reportDoc.id,
          driverReportInvoice: reportData.invoiceNumber,
          updatedAt: new Date(),
        });

        await batch.commit();
      } catch (error) {
        // Don't throw - the report itself was generated and uploaded fine.
        console.error(`Error settling delivered package ${pkg.id}:`, error);
      }
    }

    console.log(`Settled ${deliveredPackages.length} delivered packages (${paymentsCreated} payments created, ${paymentsAnnotated} existing annotated)`);

    // Canceled: recorded on the report, never paid.
    const unpaidPackages = driverPackages.filter(
      pkg => (pkg.status || '').toLowerCase() !== "delivered"
    );

    if (unpaidPackages.length > 0) {
      try {
        await Promise.all(unpaidPackages.map(pkg => updateDoc(doc(db, "packages", pkg.id), {
          // Status is left exactly as it was - a canceled package stays
          // canceled. Only the report linkage is added.
          driverReportId: reportDoc.id,
          driverReportInvoice: reportData.invoiceNumber,
          updatedAt: new Date(),
        })));
        console.log(`Linked ${unpaidPackages.length} canceled packages to the report`);
      } catch (error) {
        console.error('Error linking canceled packages:', error);
      }
    }

    // Return both the report data and the PDF blob for local download
    return { 
      reportId: reportDoc.id, 
      downloadURL, 
      reportData, 
      pdfBlob, // Add this for local download
      fileName // Add this for the download filename
    };

  } catch (error) {
    console.error('Report generation error:', error);
    throw error;
  }
};

const formatPaymentMethod = (method) => {
  switch (method) {
    case 'credit_card':
      return 'Credit Card';
    case 'bank_transfer':
      return 'Bank Transfer';
    case 'cash':
      return 'Cash';
    case 'linx':
      return 'Linx';
    case 'check':
      return 'Check';
    default:
      return method ? method.charAt(0).toUpperCase() + method.slice(1) : 'N/A';
  }
};